import * as React from 'react';
import type { PiletApi } from '@chess/platform-web';
import type { ReviewState } from '@chess/api-review';
import { useSelect } from '@chess/api-review/react';
import { useConstraint, useConstraintOwner, useConstraintRegistry } from '@chess/api-constraints/react';
import { stepControlsView, type StepAction } from './controls.ts';
import { isTyping, keyAction } from './keys.ts';
import { StepControls } from './StepControls.tsx';
import { useReview } from './use-review.ts';
import './step-controls.css';

// The steps under the board, after v1's ReviewStepControls: a bar of three
// zones. The centre has the four buttons to move through the game: the
// inner two step, the outer two go to the previous and the next of the
// reader's mistakes, or to the start and the end when no mistake lies that
// way (outer-steps.ts, 2026-10-13; ↑ and ↓ do the same); the
// right has the contextual exit; the left is empty and as wide as the
// right, so the centre never shifts when the exit comes or goes. The arrow
// keys do the same as the centre: left and right step, up and down jump to
// either end. When the reader has left the game for a line of their own, the
// forward two are disabled while the back two stay: back takes back the
// line's last move, one at a time, so another move can be tried at any point
// (the last take-back lands on the branch ply), and start drops the line.
// The exit is then "Back to the game", which returns to the ply the line
// branched from; Escape does the same. The board's Controls constraint
// switches the directions off, or, with a range (a puzzle), makes them look
// along the path within it; the session enforces it, so the buttons call
// the usual steps and only show what the constraint allows: while an owner
// holds the controls (a puzzle) the centre draws only look back (‹) and back
// to the puzzle (↶), first, next and end left out rather than greyed, so the
// puzzle's controls read as a different mode, and the exit is "Back to the
// puzzle" while looking back. Reads the review interface and the constraint
// registry; owns nothing. What is drawn, enabled and said is decided in
// `controls.ts`, drawn by `StepControls.tsx`; what a key does is in `keys.ts`.

const IDLE: ReviewState = { status: 'idle', ply: 0 };

export function setup(app: PiletApi) {
  const Panel: React.FC = () => {
    const review = useReview(app);
    const state = useSelect(review, (s) => s) ?? IDLE;
    const registry = useConstraintRegistry(app);
    const controls = useConstraint(registry, state.scope, 'controls');
    const owner = useConstraintOwner(registry, state.scope);
    const view = stepControlsView(state, controls, owner);
    const rangedRef = React.useRef(view.ranged);
    rangedRef.current = view.ranged;
    const outerRef = React.useRef(view.outer);
    outerRef.current = view.outer;
    // The outer buttons (and ↑/↓) go to the previous or next mistake, or to
    // an end when none lies that way (`view.outer`); without a game's stops
    // (held, on a line) they are the plain ends.
    const perform = React.useCallback(
      (action: StepAction) => {
        if (!review) return;
        if (action === 'outerBack' || action === 'outerForward') {
          const outer = outerRef.current;
          if (outer) review.go(action === 'outerBack' ? outer.back.ply : outer.forward.ply);
          else review[action === 'outerBack' ? 'toStart' : 'toEnd']();
          return;
        }
        review[action]();
      },
      [review],
    );

    React.useEffect(() => {
      if (!review) return;
      const onKey = (event: KeyboardEvent) => {
        const mods = { meta: event.metaKey, ctrl: event.ctrlKey, alt: event.altKey, defaultPrevented: event.defaultPrevented };
        const action = keyAction(event.key, mods, isTyping(event.target as HTMLElement | null), review.state(), rangedRef.current);
        if (!action) return;
        perform(action);
        event.preventDefault();
      };
      document.addEventListener('keydown', onKey);
      return () => document.removeEventListener('keydown', onKey);
    }, [review, perform]);

    const onAction = perform;

    return (
      <StepControls view={{ ...view, ready: view.ready && !!review }} onAction={onAction}>
        {/* Beneath the steps: whatever other plugins put at the foot, the material strip first. */}
        <app.Extension name="review.stepControlsFoot" />
      </StepControls>
    );
  };
  app.registerExtension('review.stepControls', Panel);
}
